import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface ReportSectionProps {
  title: string;
  description?: string;
  icon: ReactNode;
  /** Something for the header's right side — a link, a total. */
  aside?: ReactNode;
  children: ReactNode;
  className?: string;
}

/**
 * One panel of the report: an icon tile, a title and a line of context, then
 * the content. The same header the overview's panels draw, so the report reads
 * as part of the console rather than a separate tool.
 */
export function ReportSection({ title, description, icon, aside, children, className }: ReportSectionProps) {
  const headingId = `report-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

  return (
    <section className={cn('card flex flex-col gap-4 p-5', className)} aria-labelledby={headingId}>
      <header className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span
            className="grid h-9 w-9 shrink-0 place-items-center rounded-field bg-accent-soft text-accent"
            aria-hidden
          >
            {icon}
          </span>
          <div>
            <h2 id={headingId} className="text-base font-[650] text-ink">
              {title}
            </h2>
            {description && <p className="mt-0.5 text-xs text-ink-3">{description}</p>}
          </div>
        </div>
        {aside}
      </header>
      {children}
    </section>
  );
}
